import { logUsage } from 'owa-analytics';
import getFolderTable from '../selectors/getFolderTable';
import { orchestrator } from 'satcheljs';
import {
    updateFolderPermissionsInStore,
    updateFolderPermissions,
    updateFolderPermissionsFailed,
    updateFolderPermissionsSuccess,
} from '../actions/publicActions';
import { arePermissionArraysEqual } from '../util/compareFolderPermissions';
import type FolderType from 'owa-service/lib/contract/Folder';
import type {
    MailboxInfoInput,
    Permission,
    PermissionInput,
    Permissions,
    PermissionsInput,
} from 'owa-graph-schema';
import * as trace from 'owa-trace';
import { getAggregationBucket } from 'owa-analytics-actions';
import type DistinguishedFolderIdName from 'owa-service/lib/contract/DistinguishedFolderIdName';
import getFullFolderId from '../util/getFullFolderId';
import { getApolloClient } from 'owa-apollo';
import { UpdateFolderPermissionsDocument } from '../graphql/__generated__/UpdateFolderPermissionsMutation.interface';

/**
 * An orchestrator that updates folder permissions on server.
 * @param actionMessage Object containing folderId and permissions
 */
export default orchestrator(updateFolderPermissions, async actionMessage => {
    const { folderId, permissions, mailboxInfo } = actionMessage;
    // If folder permissions are not modified then we will return.
    const folder: FolderType = getFolderTable().get(folderId);
    const savedPermissions = folder.PermissionSet?.Permissions;
    if (arePermissionArraysEqual(savedPermissions, permissions)) {
        updateFolderPermissionsSuccess();
        return;
    }

    let success;
    try {
        success = await exportedHelperFunctions.invokeUpdateFolderPermissionsMutation(
            folderId,
            {
                Permissions: permissions,
            },
            mailboxInfo
        );
    } catch (error) {
        updateFolderPermissionsFailed();
        trace.errorThatWillCauseAlert(
            'Update folder permission on server call failed with error ' + error
        );
        return;
    }

    // Update folder permission in store if it is succesfully updated on server
    if (success) {
        updateFolderPermissionsInStore(folderId, permissions);
        updateFolderPermissionsSuccess();
        // Log folder permissions.
        logFolderPermissions(permissions);
    } else {
        updateFolderPermissionsFailed();
        trace.errorThatWillCauseAlert('Update folder permission on server call failed');
    }
});

/**
 * Execute update folder permissions mutation
 * @param folderId folder id for which folder permissions needs to be fetched.
 * @param permissionSet new permission set that needs to be saved at server.
 * @returns Promise resolved with mutation success
 */
const invokeUpdateFolderPermissionsMutationFn = async function invokeUpdateFolderPermissionsMutation(
    folderId: string | DistinguishedFolderIdName,
    permissionSet: Permissions,
    mailboxInfo: MailboxInfoInput
): Promise<boolean> {
    const fullFolderId = getFullFolderId(folderId);
    const permissionSetInput = convertPermissionSetToPermissionSetInput(permissionSet);

    const client = getApolloClient();
    const result = await client.mutate({
        variables: {
            id: fullFolderId,
            permissionSet: permissionSetInput,
            mailboxInfo,
        },
        mutation: UpdateFolderPermissionsDocument,
    });
    return !!result.data.updateFolderPermissions;
};

function convertPermissionToPermissionInput(permission: Permission): PermissionInput {
    // Permission and PermissionInput are identical except for __typename
    const newPermission = { ...permission };
    delete newPermission.__typename;
    if (newPermission.UserId) {
        const newUserId = { ...newPermission.UserId };
        delete newUserId.__typename;
        newPermission.UserId = newUserId;
    }
    return newPermission as PermissionInput;
}

function convertPermissionSetToPermissionSetInput(permissions: Permissions): PermissionsInput {
    return {
        Permissions: permissions.Permissions?.map(perm => convertPermissionToPermissionInput(perm)),
    };
}

/**
 * This helper method is exported so it can be mocked for unit testing
 */
export const exportedHelperFunctions = {
    invokeUpdateFolderPermissionsMutation: invokeUpdateFolderPermissionsMutationFn,
};

/**
 * Log folder permissions details.
 * @param permissions Array of permissions
 */
function logFolderPermissions(permissions: Permission[]) {
    const permissionsCount = permissions.length;
    const permissionLevelCounts = {}; // Variable to store count of permission levels in folder permissions.
    let defaultPermission; // Default permission. Note: Permissions assigned to Default and anonymous users can not be deleted.
    let anonymousPermission; // Anonymous permission.
    permissions.forEach(permission => {
        const { PermissionLevel, UserId } = permission;
        if (UserId.DistinguishedUser === 'Default') {
            defaultPermission = permission;
        } else if (UserId.DistinguishedUser === 'Anonymous') {
            anonymousPermission = permission;
        }
        if (!permissionLevelCounts[PermissionLevel]) {
            permissionLevelCounts[PermissionLevel] = 0;
        }
        permissionLevelCounts[PermissionLevel] += 1;
    });
    logUsage('Triage_FolderPermissions_Edit', {
        ...{
            owa_1: getAggregationBucket({ value: permissionsCount }),
            owa_2: defaultPermission.PermissionLevel,
            owa_3: anonymousPermission.PermissionLevel,
        },
        ...permissionLevelCounts,
    });
}
